import { useMemo } from "react";
import { initialsOf } from "@/components/common/UserInitials";
import { useAuth } from "@/context/AuthContext";
import { formatActivityShort, activityPillStyle } from "@/lib/dates";
import { TaskActivityHovercard } from "@/components/documents/ActivityHovercard";
import { useNavigate } from "@/hooks/useDeskNavigate";
import {
  CheckSquare, Paperclip, MessageCircle, MapPin, UserCircle2,
  AlertTriangle,
} from "lucide-react";
import PriorityBadge from "./PriorityBadge";
import { STATUS_LABEL, STATUS_COLOR } from "./StatusBadge";
import UserAvatar from "@/components/ui/UserAvatar";
import { AvatarBlock } from "@/components/settings/AccountSection";
import { MODULE_COLORS } from "@/lib/moduleColors";
import { DeadlineInline } from "./DeadlineDisplay";

/**
 * TaskKanbanView — 7 columns, one per effective_status. Each card is
 * information-dense so the reviewer can scan the board without opening
 * a single detail page:
 *
 *   ┌───────────────────────────────────────────────────┐
 *   │  TH0034/26         [Urgent]                       │  ← ref + priority
 *   │  Envoyer contrat client Djamel                    │  ← title
 *   │  📍 Villa F4                                       │  ← destination
 *   │  👤 Créé par Hamza · il y a 2h                     │  ← author + rel time
 *   │  ☑ 1/2   💬 3   📎 0                                │  ← content counters
 *   │  ─────────────────────────────────────────────    │
 *   │  ●●●●  +2                              📅 15/07  │  ← avatars + deadline
 *   └───────────────────────────────────────────────────┘
 */
const YELLOW = MODULE_COLORS.tasks;

// Spec iter124 pt.2 (Feb 2026) — colonne "completed" (Terminée) retirée
// (statut phasé out depuis iter122/iter123). 7 colonnes restantes.
// "En retard" est une colonne dédiée pour les cartes dont
// effective_status = "late". Le badge « En retard » est également
// rendu sur la carte pour rester visible quand l'utilisateur consulte
// la colonne du statut sous-jacent.
const COLUMNS = ["planned", "waiting", "in_progress", "late", "to_verify", "redo", "closed"];

const fmtShort = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) : null;

/**
 * Convert an ISO timestamp into a compact French relative time string:
 *   `< 1 min`  → "à l'instant"
 *   `< 1 h`    → "il y a 12 min"
 *   `< 1 d`    → "il y a 3h"
 *   `< 30 d`   → "il y a 5j"
 *   else       → "il y a 2 mois" / "il y a 3 ans"
 */
// Spec iter279 — l'horodatage relatif « il y a 2h » est remplacé par la
// pastille compacte partagée (lib/dates.js : formatActivityShort +
// activityPillStyle), identique aux colonnes « Activité » Tâches/Documents.

/** Deprecated (spec rev151, 2026-02-07) — remplacé par DeadlineInline
 *  du module DeadlineDisplay.jsx pour partager la palette rev150 avec
 *  la vue Liste. Fonction conservée pour rétrocompat mais plus utilisée. */
// eslint-disable-next-line no-unused-vars
function DeadlinePill() { return null; }

function AvatarStack({ assignments, usersById }) {
  if (!assignments || assignments.length === 0) {
    return <span className="text-[10px] text-muted-foreground italic">Aucun assigné</span>;
  }
  const shown = assignments.slice(0, 4);
  const extra = assignments.length - shown.length;
  return (
    <div className="flex items-center" data-testid="kanban-avatar-stack">
      {shown.map((a, i) => {
        const u = usersById[a.user_id];
        const initials = u
          ? initialsOf(u)
          : "?";
        return (
          <div
            key={a.id}
            className="rounded-full ring-2 ring-card"
            style={{ marginLeft: i === 0 ? 0 : -8, zIndex: 10 - i }}
            title={u ? `${u.first_name} ${u.last_name}` : ""}
          >
            {u ? <UserAvatar user={u} size={22} showPopover={false} testid={`kanban-avatar-${a.user_id}`} /> : <AvatarBlock initials={initials} size={22} testid={`kanban-avatar-${a.user_id}`} seed={a.user_id} />}
          </div>
        );
      })}
      {extra > 0 && (
        <span
          className="ml-1 text-[10px] text-muted-foreground font-medium tabular-nums"
          data-testid="kanban-avatar-more"
        >+{extra}</span>
      )}
    </div>
  );
}

/**
 * Counter — Puce compacte d'affichage d'une métrique (checklist / messages /
 * pièces jointes) sur les cartes Kanban.
 *
 * Spec rev53 (2026-08-02) — cohérence avec la vue Liste (rev52) :
 *   • Checklist + Pièces jointes → rond plein jaune saturé (#F9BC26) +
 *     icône sombre (#1a1a1a) quand la métrique est renseignée.
 *   • Discussion → rond plein vert WhatsApp (#25D366) + icône sombre.
 *   • État vide/inactif → gris translucide (`rgba(107,114,128,0.10)`).
 * Le compteur numérique est rendu à droite du rond en `tabular-nums`.
 */
function Counter({ Icon, value, testId, title, active, variant = "yellow" }) {
  const palettes = {
    yellow: {
      bg: "#F9BC26",
      color: "#1a1a1a",
      border: "rgba(249,188,38,0.85)",
    },
    green: {
      bg: "#25D366",
      color: "#1a1a1a",
      border: "rgba(37,211,102,0.85)",
    },
  };
  const p = palettes[variant] || palettes.yellow;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] tabular-nums"
      style={{ color: active ? "#EDEDED" : "rgba(255,255,255,0.5)" }}
      title={title}
      data-testid={testId}
    >
      <span
        className="inline-flex items-center justify-center rounded-full"
        style={{
          width: 22, height: 22,
          background: active ? p.bg : "rgba(107,114,128,0.10)",
          color: active ? p.color : "#6B7280",
          border: "1px solid " + (active ? p.border : "rgba(107,114,128,0.25)"),
        }}
      >
        <Icon size={11} strokeWidth={2.6} />
      </span>
      {value}
    </span>
  );
}

export default function TaskKanbanView({ items, users, projects = [] }) {
  const { user: viewer } = useAuth();
  const viewerIsAdmin = viewer?.role === "admin" || !!viewer?.is_first_admin;
  const navigate = useNavigate();
  const usersById = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])),
    [users]
  );
  const projectsById = useMemo(
    () => Object.fromEntries(projects.map((p) => [p.id, p])),
    [projects]
  );
  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c, []]));
    for (const t of items) {
      // 2026-02-18 : the "En retard" column groups cards whose
      // effective_status is `late` (deadline passed). Everything else
      // is bucketed by the raw stored status.
      const eff = t.effective_status || t.status || "waiting";
      const key = COLUMNS.includes(eff) ? eff : "waiting";
      g[key].push(t);
    }
    return g;
  }, [items]);

  return (
    // Spec iter171 pt.7 (2026-02-27) — 7 colonnes exactement (planned →
    // closed) réparties équitablement en `grid-cols-7` sur écrans ≥ xl.
    // L'ancienne valeur `xl:grid-cols-8` allouait 8 slots pour 7 colonnes
    // → la 8ᵉ colonne restait vide et gaspillait ~12,5 % de la largeur.
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="task-kanban-view">
      {COLUMNS.map((c) => (
        <div key={c} className="rounded-lg border border-vertec-borderDark bg-white/[0.02] overflow-hidden">
          <div
            className="px-3 py-2 text-xs uppercase font-semibold tracking-wider flex items-center justify-between"
            style={{ background: `${STATUS_COLOR[c]}1F`, color: STATUS_COLOR[c] }}
          >
            <span>{STATUS_LABEL[c]}</span>
            <span className="text-[10px]">{grouped[c].length}</span>
          </div>
          <div className="p-2 space-y-2 min-h-[100px] max-h-[70vh] overflow-y-auto">
            {grouped[c].map((t) => {
              const done = t.checklist_done ?? 0;
              const totalCk = t.checklist_total ?? (t.checklist?.length || 0);
              const nAtt = t.attachments_count ?? 0;
              const nMsg = t.messages_count ?? 0;
              const author = usersById[t.created_by];
              const authorName = author ? `${author.first_name} ${author.last_name}` : "—";
              const project = projectsById[t.project_id];
              const isLate = t.effective_status === "late";
              // Solo task = only assignee is the creator (spec 2026-02-18 point 5).
              const isSolo = !viewerIsAdmin && (t.assignments || []).every((a) => a.user_id === t.created_by);
              return (
                <button
                  key={t.id}
                  onClick={() => navigate(`/taches/${t.id}`)}
                  className="w-full text-left bg-card border border-vertec-borderDark rounded-md p-2.5 hover:border-vertec-blue transition-colors"
                  data-testid={`kanban-card-${t.id}`}
                >
                  {/* --- Row 1 : reference (yellow pill) + priority ------- */}
                  <div className="flex items-center justify-between mb-1.5 gap-1.5">
                    <span
                      className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums shrink-0"
                      style={{ background: YELLOW, color: "#1B1B1B" }}
                      data-testid={`kanban-ref-${t.id}`}
                    >{t.reference || "—"}</span>
                    <PriorityBadge priority={t.priority} />
                  </div>

                  {/* Spec iter123 pt.3 (Feb 2026) — pill « En retard » sur
                      sa propre ligne, aligné sur le style homogène (icône
                      colorée + texte même couleur, sans fond ni bordure).
                      Placé au-dessus du titre pour rester bien visible. */}
                  {isLate && (
                    <div className="mb-1.5">
                      <span
                        className="inline-flex items-center gap-1 whitespace-nowrap animate-pulse"
                        style={{
                          color: "#e07a7a",
                          fontWeight: 600,
                          fontSize: 12,
                          lineHeight: 1.1,
                        }}
                        data-testid={`kanban-late-${t.id}`}
                        title="Deadline dépassée"
                      >
                        <AlertTriangle size={13} strokeWidth={2.3} /> En retard
                      </span>
                    </div>
                  )}

                  {/* --- Title ------------------------------------------- */}
                  <div className="text-sm font-medium mb-1.5 line-clamp-2 leading-snug">
                    {t.subject}{t.indice && <><span className="font-normal" style={{ color: "#a4a4ae" }}> : </span><span style={{ color: "#f4c877", fontWeight: 500 }} data-testid={`kanban-indice-${t.id}`}>{t.indice}</span></>}
                  </div>

                  {/* --- Destination ------------------------------------ */}
                  <div className="flex items-center gap-1 mb-1.5 text-[11px]"
                       style={{ color: project ? "#EDEDED" : "rgba(255,255,255,0.4)" }}
                       data-testid={`kanban-destination-${t.id}`}
                  >
                    <MapPin size={11} strokeWidth={2.2} />
                    <span className="truncate">
                      {project ? project.name : "— Aucune destination"}
                    </span>
                  </div>

                  {/* --- Author + relative created_at ------------------- */}
                  <div className="flex items-center gap-1.5 mb-2 text-[11px] text-muted-foreground">
                    <UserCircle2 size={12} strokeWidth={2.2} />
                    <span className="truncate">
                      Créé par <span className="text-foreground/80">{authorName}</span>
                    </span>
                    {(t.updated_at || t.created_at) && (
                      <span className="ml-auto shrink-0" onClick={(e) => e.stopPropagation()}>
                        <TaskActivityHovercard task={t} user={usersById?.[t.activity?.actor_id]} usersById={usersById}>
                          <span
                            className="inline-flex items-center tabular-nums shrink-0"
                            style={activityPillStyle(t.activity?.at || t.updated_at || t.created_at)}
                            data-testid={`kanban-relative-${t.id}`}
                          >
                            {formatActivityShort(t.activity?.at || t.updated_at || t.created_at)}
                          </span>
                        </TaskActivityHovercard>
                      </span>
                    )}
                  </div>

                  {/* --- Compact metadata (always displayed) ------------ */}
                  <div
                    className="flex items-center gap-3 mb-2"
                    data-testid={`kanban-counters-${t.id}`}
                  >
                    <Counter
                      Icon={CheckSquare}
                      value={`${done}/${totalCk}`}
                      testId={`kanban-checklist-${t.id}`}
                      title="Checklist"
                      active={totalCk > 0}
                      variant="yellow"
                    />
                    <Counter
                      Icon={MessageCircle}
                      value={nMsg}
                      testId={`kanban-messages-${t.id}`}
                      title={isSolo ? "Tâche solo — pas de discussion" : "Messages"}
                      active={nMsg > 0 && !isSolo}
                      variant="green"
                    />
                    <Counter
                      Icon={Paperclip}
                      value={nAtt}
                      testId={`kanban-attachments-${t.id}`}
                      title="Pièces jointes"
                      active={nAtt > 0}
                      variant="yellow"
                    />
                  </div>

                  {/* --- Footer : avatars ▪ deadline --------------------- */}
                  <div
                    className="flex items-center justify-between pt-2 border-t"
                    style={{ borderColor: "rgba(255,255,255,0.08)" }}
                  >
                    <AvatarStack assignments={t.assignments} usersById={usersById} />
                    <DeadlineInline iso={t.deadline} size={22} />
                  </div>
                </button>
              );
            })}
            {grouped[c].length === 0 && (
              <div className="text-[11px] text-muted-foreground text-center py-6">Vide</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
